export const getUserFriendlyErrorMessage = (backendMessage: string): string => {
  const errorMappings: Record<string, string> = {
    // Authentication errors
    'Invalid authentication. Can specify either apiKey= OR apiUser= and apiPass authentication, but not both.':
      'Please use either an API key or username/password, but not both.',

    'Invalid authentication. Must either provide API key as apiKey=, or username/password authenticate using apiUser= and apiPass=':
      'Please provide either an API key or username and password to sign in.',

    'Invalid authentication. Only Basic authentication via Authorization header is supported.':
      'Authentication method not supported. Please use the login form.',

    'Invalid API username/password or account is disabled':
      'Invalid email or password. Please check your credentials and try again.',

    'API Key not found, is disabled, or does not have permission to access the requested resource(s).':
      'Your API key is invalid or has expired. Please contact your administrator.',

    // Standard API Response Errors
    'Incorrect email and/or password':
      'Invalid email or password. Please check your credentials and try again.',

    'Missing Authorization Token':
      'Your session has expired. Please sign in again.',

    'Invalid Authorization Token':
      'Your session is invalid. Please sign in again.',

    'Request body was not valid JSON': 'Invalid data format. Please try again.',

    'Invalid request parameter':
      'Invalid request. Please check your input and try again.',

    'Missing request parameter':
      'Required information is missing. Please fill in all required fields.',

    'Invalid posted data':
      'The data you submitted is invalid. Please check your input and try again.',

    'Invalid Token / id': 'Invalid session. Please sign in again.',

    'Missing resource':
      'The requested item could not be found. It may have been moved or deleted.',

    'Content-Length header was not provided':
      'Upload failed. Please try again.',

    'Request body too large':
      'The file you uploaded is too large. Please choose a smaller file.',

    'Resource was modified meanwhile':
      'This item has been changed by someone else. Please refresh and try again.',

    'Method not allowed on this end point':
      'This action is not allowed. Please contact support if you need assistance.',

    'Requested version not available on this server':
      'The requested version is not available. Please try a different version.',

    'Client has sent too many requests':
      'Too many requests. Please wait a moment and try again.',

    "Resource's access forbidden for this user":
      'You do not have permission to access this resource.',

    'Another resource violates constraint':
      'This action conflicts with existing data. Please check your input and try again.',

    'Internal Server Error': 'A server error occurred. Please try again later.',

    'Service Temporary unavailable due to high load':
      'The service is temporarily unavailable due to high traffic. Please try again later.',

    'Service deprecated':
      'This feature is no longer available. Please use the updated version.',

    'No deletion allowed':
      'This item cannot be deleted. Please contact support if you need assistance.',

    // Custom Field Errors
    'Field in use':
      'This field is currently being used and cannot be modified.',

    "Field doesn't support options":
      "This field type does not support the options you're trying to add.",

    // Role Management Errors
    'The role is not editable':
      'This role cannot be edited. Please contact your administrator.',

    'The role is not deletable':
      'This role cannot be deleted. Please contact your administrator.',

    'Role is attached to one or more users. It can not be deleted':
      'This role cannot be deleted because it is assigned to users. Please reassign users first.',

    // Automation Job Errors
    'The job is locked': 'This job is currently locked and cannot be modified.',

    'The job can not to be completed. Current status:':
      'This job cannot be completed in its current state.',

    'The job is completed': 'This job has already been completed.',

    'Invalid progress value:':
      'Invalid progress value. Please enter a valid percentage.',

    // Value List Errors
    'Unknown value list:': 'The requested value list could not be found.',

    'Unknown most recent list:': 'The requested list could not be found.',

    // Client/Project Errors
    'No Client': 'No client information found. Please contact support.',

    // Report Errors
    "The report doesn't support output":
      'This report format is not supported. Please choose a different format.',

    // Library/Download Errors
    'No data to download': 'No data is available for download.',

    // Media Errors
    'Media exists. The object is not versionable.':
      'This media file already exists and cannot be replaced.',

    "Invalid image size. Must be one of 'thumb', 'orig', 'medium'":
      'Invalid image size. Please choose a supported size.',

    // Ajax Controller Errors
    'Could not find custom field':
      'The requested custom field could not be found.',

    'An option with key already exists.':
      'An option with this key already exists. Please choose a different key.',

    'Could not find custom field option with id=':
      'The requested option could not be found.',

    'This option cannot be deleted because it is in use times.':
      'This option cannot be deleted because it is currently in use.',

    'Could not find item': 'The requested item could not be found.',

    // Validation Errors
    'The identifier has already been taken.':
      'This identifier is already in use. Please choose a different one.',

    'The identifier field is required.': 'Please provide an identifier.',

    'The identifier format is invalid.':
      'Please enter a valid identifier format.',

    // General Exception Messages
    'Unknown error.': 'An unexpected error occurred. Please try again.',

    'Not Found': 'The requested resource could not be found.',

    'You are not authorized for this action.':
      'You do not have permission to perform this action.',

    'Wrong path:': 'Invalid path. Please check the URL and try again.',

    'No tag field': 'No tag field found. Please contact support.',

    'No tag:': 'The requested tag could not be found.',

    'No custom field:': 'The requested custom field could not be found.',

    'No MetaData:': 'The requested metadata could not be found.',

    'UPC already uploaded:': 'This UPC has already been uploaded.',

    'Wrong field name:': 'Invalid field name. Please check your input.',

    // Upload/Processing Errors
    'Error to save object data': 'Failed to save the data. Please try again.',

    'zbarimg not installed.':
      'Barcode processing is not available. Please contact support.',

    "Object must implement '\\App\\Traits\\HasMedia'":
      'This object does not support media uploads.',

    'Failed to load file':
      'Failed to load the file. Please check the URL and try again.',

    'Custom Filter is not defined:': 'The requested filter could not be found.',

    'Model, Query or Callback': 'Invalid data format. Please check your input.',

    'Attemped to set custom value for key':
      'Invalid custom value. Please check the format and try again.',

    // MFA errors
    'MFA token is required':
      'Please enter your two-factor authentication code.',

    'MFA token is incorrect':
      'Invalid two-factor authentication code. Please try again.',

    // Generic authentication errors
    Unauthorized:
      'You are not authorized to access this resource. Please sign in again.',

    Forbidden: 'You do not have permission to perform this action.',

    'Token expired': 'Your session has expired. Please sign in again.',

    'Invalid token': 'Your session is invalid. Please sign in again.',

    // File upload errors
    'File too large':
      'The file you uploaded is too large. Please choose a smaller file.',

    'Invalid file type':
      'The file type is not supported. Please choose a different file.',

    // Network errors
    'Network Error':
      'Unable to connect to the server. Please check your internet connection and try again.',

    'Request timeout':
      'The request took too long to complete. Please try again.',

    // Permission errors
    'Insufficient permissions':
      'You do not have permission to perform this action. Please contact your administrator.',

    'Access denied':
      'Access denied. You may not have the required permissions.',
  };

  // Try exact match first
  if (errorMappings[backendMessage]) {
    return errorMappings[backendMessage];
  }

  // Try partial matches for more flexible error handling
  for (const [key, value] of Object.entries(errorMappings)) {
    if (backendMessage.toLowerCase().includes(key.toLowerCase())) {
      return value;
    }
  }

  // If no match found, return the original message for development
  // In production, return a generic message
  const isProduction =
    window.location.hostname.includes('prod') ||
    window.location.hostname.includes('staging');

  return isProduction ? 'An error occurred. Please try again.' : backendMessage;
};

/**
 * Get authentication-specific friendly error messages
 */
export const getAuthFriendlyErrorMessage = (backendMessage: string): string => {
  const authErrorMappings: Record<string, string> = {
    'Invalid authentication. Can specify either apiKey= OR apiUser= and apiPass authentication, but not both.':
      'Please use either an API key or username/password, but not both.',

    'Invalid authentication. Must either provide API key as apiKey=, or username/password authenticate using apiUser= and apiPass=':
      'Please provide either an API key or username and password to sign in.',

    'Invalid authentication. Only Basic authentication via Authorization header is supported.':
      'Authentication method not supported. Please use the login form.',

    'Invalid API username/password or account is disabled':
      'Invalid email or password. Please check your credentials and try again.',

    'API Key not found, is disabled, or does not have permission to access the requested resource(s).':
      'Your API key is invalid or has expired. Please contact your administrator.',

    'Incorrect email and/or password':
      'Invalid email or password. Please check your credentials and try again.',

    'Missing Authorization Token':
      'Your session has expired. Please sign in again.',

    'Invalid Authorization Token':
      'Your session is invalid. Please sign in again.',

    'Invalid Token / id': 'Invalid session. Please sign in again.',

    'MFA token is required':
      'Please enter your two-factor authentication code.',

    'MFA token is incorrect':
      'Invalid two-factor authentication code. Please try again.',

    Unauthorized:
      'You are not authorized to access this resource. Please sign in again.',

    Forbidden: 'You do not have permission to perform this action.',

    'Token expired': 'Your session has expired. Please sign in again.',

    'Invalid token': 'Your session is invalid. Please sign in again.',
  };

  if (authErrorMappings[backendMessage]) {
    return authErrorMappings[backendMessage];
  }

  for (const [key, value] of Object.entries(authErrorMappings)) {
    if (backendMessage.toLowerCase().includes(key.toLowerCase())) {
      return value;
    }
  }

  return 'Authentication failed. Please check your credentials and try again.';
};
